<template>
  <div
    v-if="editor"
    :class="['editor-wrapper', wrapperClass, {'error-outline': isError}]"
  >
    <div
      v-if="!disabled"
      id="editor-toolbar"
      class="editor-toolbar row"
      @pointerdown.prevent
    >
      <hr-toolbar-btn
        icon="format_bold"
        :active="editor.isActive('bold')"
        :tooltip="$t('editor.bold')"
        shortcut="MOD + B"
        @click="editor.chain().focus().toggleBold().run()"
      />
      <hr-toolbar-btn
        v-if="!isArabicLang"
        icon="format_italic"
        :active="editor.isActive('italic')"
        :tooltip="$t('editor.italic')"
        shortcut="MOD + I"
        @click="editor.chain().focus().toggleItalic().run()"
      />
      <hr-toolbar-btn
        icon="format_underlined"
        :active="editor.isActive('underline')"
        :tooltip="$t('editor.underline')"
        shortcut="MOD + U"
        @click="editor.chain().focus().toggleUnderline().run()"
      />
      <hr-toolbar-btn
        icon="strikethrough_s"
        :active="editor.isActive('strike')"
        :tooltip="$t('editor.strike')"
        shortcut="MOD + SHIFT + X"
        @click="editor.chain().focus().toggleStrike().run()"
      />

      <hr-divider vertical />

      <hr-toolbar-btn
        icon="format_align_left"
        @click="onAlignElement('left')"
      />
      <hr-toolbar-btn
        icon="format_align_center"
        @click="onAlignElement('center')"
      />
      <hr-toolbar-btn
        icon="format_align_right"
        @click="onAlignElement('right')"
      />
      <hr-toolbar-btn
        icon="format_align_justify"
        @click="onAlignElement('justify')"
      />
      <hr-divider vertical />

      <hr-toolbar-btn
        v-if="horizontalRule"
        icon="horizontal_rule"
        @click="editor.chain().focus().setHorizontalRule().run()"
      />
      <hr-toolbar-btn
        v-if="link"
        icon="link"
        :active="editor.isActive('link')"
        :tooltip="$t('editor.modals.link.title')"
        :disable="isSelectionEmpty"
        @click="openLinkMenu"
      >
        <text-editor-link
          v-model="showLinkModal"
          :editor="editor"
        />
      </hr-toolbar-btn>
      <hr-toolbar-btn
        v-if="link && editor.isActive('link')"
        icon="link_off"
        :tooltip="$t('editor.unsetLink')"
        @click="editor.chain().focus().unsetLink().run()"
      />

      <hr-divider
        v-if="horizontalRule || link"
        vertical
      />

      <hr-toolbar-btn
        icon="format_list_bulleted"
        :active="editor.isActive('bulletList')"
        :tooltip="$t('editor.unorderedList')"
        shortcut="MOD + SHIFT + 8"
        @click="editor.chain().focus().toggleBulletList().run()"
      />
      <hr-toolbar-btn
        icon="format_list_numbered"
        :active="editor.isActive('orderedList')"
        :tooltip="$t('editor.orderedList')"
        shortcut="MOD + SHIFT + 7"
        @click="editor.chain().focus().toggleOrderedList().run()"
      />
      <hr-toolbar-btn
        v-if="checklist"
        icon="checklist"
        :active="editor.isActive('taskList')"
        :tooltip="$t('editor.checklist')"
        shortcut="MOD + SHIFT + 9"
        data-e2e="button-editor-checklist"
        @click="editor.chain().focus().toggleTaskList().run()"
      />
      <hr-toolbar-btn
        v-if="indent"
        icon="format_indent_increase"
        :tooltip="$t('editor.increaseIndent')"
        shortcut="TAB"
        @click="editor.commands.indent()"
      />
      <hr-toolbar-btn
        v-if="indent"
        icon="format_indent_decrease"
        :tooltip="$t('editor.decreaseIndent')"
        shortcut="SHIFT + TAB"
        @click="editor.commands.outdent()"
      />

      <hr-divider vertical />

      <hr-toolbar-btn
        v-if="textColor"
        icon="palette"
        :tooltip="$t('editor.textColor')"
        :active="localTextColorVisible"
      >
        <hr-menu
          v-model="localTextColorVisible"
          @show="closeAllMenusExcept('textColor')"
        >
          <hr-color
            @change="onSetTextColor"
          />
        </hr-menu>
      </hr-toolbar-btn>
      <hr-toolbar-btn
        v-if="textHighlight"
        icon="format_paint"
        :tooltip="$t('editor.textHighlight')"
        :active="highlightColorVisible"
      >
        <hr-menu
          v-model="highlightColorVisible"
          @show="closeAllMenusExcept('highlight')"
        >
          <hr-color
            @change="onSetHighlight"
          />
        </hr-menu>
      </hr-toolbar-btn>
      <hr-toolbar-dropdown
        v-if="fontSize"
        ref="fontSizeDropdown"
        icon="format_size"
        :label="$t('editor.fontSize.title')"
        @show="closeAllMenusExcept('fontSize')"
      >
        <hr-list>
          <hr-list-item
            v-close-popup
            clickable
            :active="editor.isActive('textStyle', { fontSize: 'x-small' })"
            @click="editor.chain().focus().setFontSize('x-small').run()"
          >
            <span class="font-xsmall">
              {{ $t('editor.fontSize.verySmall') }}
            </span>
          </hr-list-item>
          <hr-list-item
            v-close-popup
            clickable
            :active="!editor.isActive('textStyle') || editor.isActive('textStyle', { fontSize: null })"
            @click="editor.chain().focus().setFontSize(null).run()"
          >
            <span class="font-small">
              {{ $t('editor.fontSize.small') }}
            </span>
          </hr-list-item>
          <hr-list-item
            v-close-popup
            clickable
            :active="editor.isActive('textStyle', { fontSize: 'medium' })"
            @click="editor.chain().focus().setFontSize('medium').run()"
          >
            <span class="font-medium">
              {{ $t('editor.fontSize.normal') }}
            </span>
          </hr-list-item>
          <hr-list-item
            v-close-popup
            clickable
            :active="editor.isActive('textStyle', { fontSize: 'large' })"
            @click="editor.chain().focus().setFontSize('large').run()"
          >
            <span class="font-large">
              {{ $t('editor.fontSize.mediumLarge') }}
            </span>
          </hr-list-item>
          <hr-list-item
            v-close-popup
            clickable
            :active="editor.isActive('textStyle', { fontSize: 'x-large' })"
            @click="editor.chain().focus().setFontSize('x-large').run()"
          >
            <span class="font-xlarge">
              {{ $t('editor.fontSize.big') }}
            </span>
          </hr-list-item>
          <hr-list-item
            v-close-popup
            clickable
            :active="editor.isActive('textStyle', { fontSize: 'xx-large' })"
            @click="editor.chain().focus().setFontSize('xx-large').run()"
          >
            <span class="font-xxlarge">
              {{ $t('editor.fontSize.veryBig') }}
            </span>
          </hr-list-item>
          <hr-list-item
            v-close-popup
            clickable
            :active="editor.isActive('textStyle', { fontSize: 'xxx-large' })"
            @click="editor.chain().focus().setFontSize('xxx-large').run()"
          >
            <span class="font-xxxlarge">
              {{ $t('editor.fontSize.max') }}
            </span>
          </hr-list-item>
        </hr-list>
      </hr-toolbar-dropdown>
      <hr-divider
        v-if="textColor || textHighlight || fontSize"
        vertical
      />
      <hr-toolbar-btn
        v-if="imageUpload"
        icon="image"
        :tooltip="$t('editor.insertImage')"
      >
        <input
          ref="file"
          type="file"
          accept="image/*"
          class="file-overlay cursor-pointer"
          @input="onUploadImage"
        >
      </hr-toolbar-btn>
      <hr-toolbar-dropdown
        v-if="tables"
        ref="tableDropdown"
        icon="fa fa-table"
        @show="closeAllMenusExcept('table')"
      >
        <hr-list>
          <hr-list-item
            :label="$t('editor.table.insertTable')"
            clickable
            :disable="isTableActive"
            @click="openTableMenu"
          >
            <template
              v-if="!isMobileDevice"
              #trailing
            >
              <hr-icon name="keyboard_arrow_right" />
            </template>
            <template #menu>
              <text-editor-insert-table
                v-model="showInsertTableModal"
                :editor="editor"
                @close="closeTableMenu"
              />
            </template>
          </hr-list-item>
          <hr-divider />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.columnBefore')"
            :disable="!isTableActive"
            @click="editor.chain().focus().addColumnBefore().run()"
          />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.columnAfter')"
            :disable="!isTableActive"
            @click="editor.chain().focus().addColumnAfter().run()"
          />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.deleteColumn')"
            :disable="!isTableActive"
            @click="editor.chain().focus().deleteColumn().run()"
          />
          <hr-divider />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.rowBefore')"
            :disable="!isTableActive"
            @click="editor.chain().focus().addRowBefore().run()"
          />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.rowAfter')"
            :disable="!isTableActive"
            @click="editor.chain().focus().addRowAfter().run()"
          />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.deleteRow')"
            :disable="!isTableActive"
            @click="editor.chain().focus().deleteRow().run()"
          />
          <hr-divider />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.mergeCells')"
            :disable="!isTableActive"
            @click="editor.chain().focus().mergeCells().run()"
          />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.splitCell')"
            :disable="!isTableActive"
            @click="editor.chain().focus().splitCell().run()"
          />
          <hr-divider />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.toggleHeaderRow')"
            :disable="!isTableActive"
            @click="editor.chain().focus().toggleHeaderRow().run()"
          />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.toggleHeaderColumn')"
            :disable="!isTableActive"
            @click="editor.chain().focus().toggleHeaderColumn().run()"
          />
          <hr-list-item
            v-close-popup
            clickable
            :label="$t('editor.table.toggleHeaderCell')"
            :disable="!isTableActive"
            @click="editor.chain().focus().toggleHeaderCell().run()"
          />
          <hr-divider />
          <hr-list-item
            v-close-popup
            clickable
            destructive
            :label="$t('editor.table.deleteTable')"
            :disable="!isTableActive"
            @click="editor.chain().focus().deleteTable().run()"
          />
        </hr-list>
      </hr-toolbar-dropdown>
      <hr-divider vertical />
      <template v-if="!noUndoRedo">
        <hr-toolbar-btn
          icon="undo"
          :tooltip="$t('editor.undo')"
          shortcut="MOD + Z"
          :disable="!editor.can().undo()"
          @click="editor.chain().focus().undo().run()"
        />
        <hr-toolbar-btn
          icon="redo"
          :tooltip="$t('editor.redo')"
          shortcut="MOD + SHIFT + Z | MOD + Y"
          :disable="!editor.can().redo()"
          @click="editor.chain().focus().redo().run()"
        />
      </template>
      <slot />
    </div>
    <editor-content
      :class="{ 'disabled-image': disabled }"
      class="custom-editor"
      :editor="editor"
    />
    <div
      v-if="isError"
      class="error-hint"
    >
      {{ $t('settings.reportTemplates.edit.mustacheError') }}
    </div>
    <hr-inner-loading
      class="z-top"
      :showing="loading"
    />
  </div>
</template>

<script>
import TextColor from '@tiptap/extension-color';
import TextHighlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { Editor, EditorContent } from '@tiptap/vue-3';
import TextEditorLink from 'components/UI/modals/TextEditorLink';
import TextEditorInsertTable from 'components/UI/TextEditorInsertTable';
import createUnchangedValueTracker from 'components/UI/unchangedValueTracker';
import { get, head } from 'lodash';
import { mapState } from 'pinia';
import { EditorState } from 'prosemirror-state';
import {
  HrColor, HrDivider, HrIcon, HrInnerLoading, HrList, HrListItem, HrMenu, HrToolbarBtn,
  HrToolbarDropdown,
} from 'src/design-system';
import { ChecklistItem, ChecklistList } from 'src/editor-extensions/checklist';
import CustomTable from 'src/editor-extensions/customTable';
import DataSection from 'src/editor-extensions/dataSection';
import DynamicField from 'src/editor-extensions/dynamicField';
import FontSize from 'src/editor-extensions/fontSize';
import Indent from 'src/editor-extensions/indent';
import NormalizePastedText from 'src/editor-extensions/normalizePastedText';
import ResizableImage from 'src/editor-extensions/resizableImage';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { mustacheValidator } from 'src/utils/AppUtils';

const HTML_ATTRIBUTE_DIR_AUTO = {
  dir: 'auto',
};

export default {
  name: 'TextEditor',

  components: {
    TextEditorInsertTable,
    TextEditorLink,
    EditorContent,
    HrColor,
    HrDivider,
    HrIcon,
    HrInnerLoading,
    HrList,
    HrListItem,
    HrMenu,
    HrToolbarBtn,
    HrToolbarDropdown,
  },

  mixins: [
    Interactions,
  ],

  provide() {
    return {
      isImageDeleted: this.isImageDeleted,
    };
  },

  props: {
    modelValue: {
      type: String,
      default: '',
    },

    validateMustache: {
      type: Boolean,
      default: false,
    },

    tables: {
      type: Boolean,
      default: false,
    },

    link: {
      type: Boolean,
      default: false,
    },

    fontSize: {
      type: Boolean,
      default: false,
    },

    textColor: {
      type: Boolean,
      default: false,
    },

    textHighlight: {
      type: Boolean,
      default: false,
    },

    horizontalRule: {
      type: Boolean,
      default: false,
    },

    indent: {
      type: Boolean,
      default: false,
    },

    checklist: {
      type: Boolean,
      default: false,
    },

    noUndoRedo: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    autofocus: {
      type: Boolean,
      default: false,
    },

    imageUpload: {
      type: Boolean,
      default: false,
    },

    wrapperClass: {
      type: String,
      default: 'q-my-md',
    },

    loading: {
      type: Boolean,
      default: false,
    },

    preserveWhitespace: {
      validator: (prop) => typeof prop === 'boolean' || (typeof prop === 'string' && prop === 'full'),
      default: 'full',
    },

    enableDynamicFields: {
      type: Boolean,
      default: false,
    },

    enableDataSection: {
      type: Boolean,
      default: false,
    },

    /**
     * Emit the given `modelValue` itself, not the editor's serialization of it, while the content is unchanged.
     * For callers that compare the string, like the note auditor.
     */
    preserveUnchangedValue: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'ready', 'focus', 'blur', 'new-image', 'image-deleted', 'error-status-update'],

  data() {
    return {
      /** @type {Editor} */
      editor: null,
      extensions: [],

      showLinkModal: false,
      showInsertTableModal: false,
      isError: null,

      /** @type {ReturnType<typeof createUnchangedValueTracker> | null} */
      unchangedValueTracker: null,

      /**
       * This object is being provided to and mutated in the nested {@link EditorResizeImageMenu} node-wrapper component
       * (with provide/inject syntax) so we could sign up to the event created by that component
       */
      isImageDeleted: {
        value: false,
      },

      localTextColorVisible: false,
      highlightColorVisible: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),
    ...mapState(network, ['networkOnline', 'offlineMode']),

    isSelectionEmpty() {
      const { selection } = this.editor.state;

      return selection.empty;
    }, // isSelectionEmpty()

    isTableActive() {
      return this.editor.isActive('table');
    },

    isMobileDevice() {
      return this.$q.platform.is.mobile || this.$q.screen.lt.sm;
    },

    isArabicLang() {
      return this.userDetails.language === 'ar';
    },
  },

  watch: {
    modelValue(value) {
      if (this.unchangedValueTracker?.value === value) {
        return;
      }

      if (this.editor.getHTML() !== value) {
        this.editor.commands.setContent(value, false, { preserveWhitespace: this.preserveWhitespace });
      }
      this.unchangedValueTracker?.load(value, this.editor.getHTML());
    },

    disabled(newValue) {
      // we explicitly set editable to make sure that EditorResizableImag.vue will reflect the changes
      this.initEditor(!newValue);
    },

    'isImageDeleted.value': {
      handler(val) {
        if (val) {
          this.$emit('image-deleted');
          this.isImageDeleted.value = false;
        }
      },
    },
  },

  mounted() {
    this.unchangedValueTracker = this.preserveUnchangedValue ? createUnchangedValueTracker() : null;
    this.initExtensions();
    this.initEditor(!this.disabled);
    this.$emit('ready');
  },

  beforeUnmount() {
    this.cleanEditor();
  },

  methods: {
    initExtensions() {
      const extensions = [
        StarterKit.configure({
          paragraph: {
            HTMLAttributes: HTML_ATTRIBUTE_DIR_AUTO,
          },
          orderedList: {
            HTMLAttributes: HTML_ATTRIBUTE_DIR_AUTO,
          },
          bulletList: {
            HTMLAttributes: HTML_ATTRIBUTE_DIR_AUTO,
          },
        }),
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph', 'image'],
        }),
        NormalizePastedText,
      ];

      if (this.enableDynamicFields) {
        extensions.push(
          DynamicField.configure({
            HTMLAttributes: { class: 'token' },
          }),
        );
      }

      if (this.enableDataSection) {
        extensions.push(
          DataSection.configure({
            HTMLAttributes: { class: 'data-section' },
          }),
        );
      }

      if (this.indent) {
        extensions.push(
          Indent.configure({
            types: ['listItem', 'paragraph'],
            minLevel: 0,
            maxLevel: 8,
          }),
        );
      }

      if (this.checklist) {
        // Placed after Indent so Checklist keymaps take priority (Tab calls sinkListItem first).
        extensions.push(ChecklistList, ChecklistItem);
      }

      if (this.tables) {
        extensions.push(
          CustomTable.configure({ resizable: true }),
          TableHeader,
          TableRow,
          TableCell,
        );
      }

      if (this.imageUpload) {
        extensions.push(
          ResizableImage.configure({
            allowBase64: true,
          }),
        );
      }

      if (this.link) {
        extensions.push(Link.configure({ openOnClick: false }));
      }

      if (this.fontSize || this.textColor) {
        extensions.push(TextStyle);
      }

      if (this.fontSize) {
        extensions.push(FontSize);
      }

      if (this.textColor) {
        extensions.push(TextColor);
      }

      if (this.textHighlight) {
        extensions.push(TextHighlight.configure({ multicolor: true }));
      }
      this.extensions = extensions;
    }, // initExtensions()

    /**
     * This logic was moved to a separate method because vue doesn't reflect changes inside of the class instance when
     * we are trying to retrieve them inside EditorResizableImage.vue. Such fields as editable are stored deep inside
     * Editor class and are NOT reactive. On this basis, the class instance should be recreated each time we want to set
     * a new valid editable state */
    initEditor(editable) {
      this.cleanEditor();

      this.editor = new Editor({
        editable,
        content: this.modelValue,
        parseOptions: {
          preserveWhitespace: this.preserveWhitespace,
        },
        extensions: this.extensions,
        autofocus: this.autofocus,
        onCreate: () => {
          this.mustacheValidation();
        },
        onUpdate: () => {
          this.mustacheValidation();

          const html = this.editor.getHTML();
          this.$emit('update:modelValue', this.unchangedValueTracker ? this.unchangedValueTracker.resolve(html) : html);
        },
        onFocus: () => {
          this.$emit('focus');
        },
        onBlur: ({ event }) => {
          const { relatedTarget } = event;

          const isToolbar = relatedTarget?.classList?.contains('editor-toolbar');
          const isWithinToolbar = relatedTarget?.closest('#editor-toolbar');

          if (isWithinToolbar || isToolbar) {
            return;
          }

          this.$emit('blur', event);
        },
      });

      // Recreated on every `disabled` toggle; the tracker outlives it so a revert still finds the original string.
      if (this.unchangedValueTracker && this.unchangedValueTracker.value !== this.modelValue) {
        this.unchangedValueTracker.load(this.modelValue, this.editor.getHTML());
      }
    }, // initEditor()

    mustacheValidation() {
      if (this.enableDynamicFields || this.validateMustache) {
        this.isError = !mustacheValidator(this.editor.getText());
        this.$emit('error-status-update', this.isError);
      }
    },

    cleanEditor() {
      if (this.editor) {
        this.editor.destroy();
      }
    }, // cleanEditor();

    focus() {
      if (this.editor) {
        this.editor.commands.focus();
      }
    }, // focus()

    onAlignElement(direction) {
      this.editor.chain().focus().setTextAlign(direction).run();
    }, // onAlignElement()

    insertDynamicField(field) {
      this.editor.commands.setDynamicField(field);
    }, // insertDynamicField()

    insertDataSection(section) {
      this.editor.commands.setDataSection(section);
    }, // insertDataSection()

    onSetHighlight(color) {
      this.editor.chain().focus().setHighlight({ color }).run();
      this.highlightColorVisible = false;
    }, // onSetHighlight()

    onSetTextColor(color) {
      this.editor.chain().focus().setColor(color).run();
      this.localTextColorVisible = false;
    }, // onSetTextColor()

    closeAllMenusExcept(name) {
      if (name !== 'link') {
        this.showLinkModal = false;
      }
      if (name !== 'textColor') {
        this.localTextColorVisible = false;
      }
      if (name !== 'highlight') {
        this.highlightColorVisible = false;
      }
      if (name !== 'fontSize') {
        this.$refs.fontSizeDropdown?.hide();
      }
      if (name !== 'table') {
        this.$refs.tableDropdown?.hide();
      }
    },

    openLinkMenu() {
      this.showLinkModal = !this.showLinkModal;
      if (this.showLinkModal) {
        this.closeAllMenusExcept('link');
      }
    },

    openTableMenu() {
      this.showInsertTableModal = true;
    },

    closeTableMenu() {
      this.showInsertTableModal = false;
      this.$refs.tableDropdown?.hide();
    },

    async onUploadImage(evt) {
      if (!this.networkOnline && !this.offlineMode) {
        this.alertDialog(
          this.$t('common.noInternetConnection'),
          this.$t('editor.image.noInternetToUpload'),
        );
        return;
      }

      const file = head(evt.target.files || evt.dataTransfer.files);
      if (!file) {
        return;
      }
      const fileSize = get(file, 'size', 0) / 1024;
      if (fileSize > 1024) {
        this.notifyNegative(this.$t('common.warningFileTooBig', { maxFileSize: '1 MB' }));
        return;
      }

      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        this.notifyNegative(this.$t('editor.image.imageTypeIsNotSupported'));
        return;
      }

      const convertBase64 = (f) => {
        return new Promise((resolve, reject) => {
          const fileReader = new FileReader();
          fileReader.readAsDataURL(f);

          fileReader.onload = () => {
            resolve(fileReader.result);
          };

          fileReader.onerror = (error) => {
            reject(error);
          };
        });
      };

      this.editor.chain().focus().setImage({ src: await convertBase64(file) }).run();
      this.$emit('new-image');

      if (this.$refs.file) {
        this.$refs.file.value = '';
      }
    }, // onUploadImage()

    clearChangesHistory() {
      if (!this.editor) {
        return;
      }

      const newEditorState = EditorState.create({
        doc: this.editor.state.doc,
        plugins: this.editor.state.plugins,
        schema: this.editor.state.schema,
      });

      this.editor.view.updateState(newEditorState);
    }, // clearChangesHistory()

  },
};
</script>

<style lang="scss">
.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}

.editor-wrapper {
  position: relative;
  border: 1px solid var(--app-outline, rgba(0,0,0,0.12));
  border-radius: 4px;
  background-color: var(--app-surface, #ffffff);

  td, th {
    white-space: pre-line;
    font-size: 14px;
  }

  .editor-toolbar {
    border-bottom: 1px solid var(--app-outline, rgba(0,0,0,0.12));
    background-color: var(--app-surface-container-high, #ffffff);
    padding: 4px;
    gap: 2px;
  }
}

.editor-wrapper.sticky-toolbar {
  .editor-toolbar {
    position: sticky;
    top: 0;
    z-index: 10;
  }
}

.editor-wrapper.carousel-editor {
  display: flex;
  flex-direction: column;
  max-height: 70vh; /* fallback for browsers without svh support */
  max-height: 70svh;
  overflow: hidden;

  .editor-toolbar {
    position: static;
    flex-shrink: 0;
  }

  .custom-editor {
    flex-grow: 1;
    overflow-y: auto;
  }
}

.custom-text-editor  {

  .editor-toolbar {
    width: 100%;
  }

  &--mobile {
    .custom-editor {
      padding: 0;
      min-width: 0;
    }
  }
}

.token {
  font-size: 0.7em;
  background: $primary-variant;
  border-radius: 3px;
  padding: 1px 3px;
  margin: 0px 1px 3px 1px;
  display: inline-flex;
  align-items: center;
  color: white;
}

.token::before {
  content: attr(data-label);
  display: inline-block;
  white-space: pre-wrap;
}

.data-section {
  font-size: 1.2em;
  background-color:$primary-variant;
  border-radius: 3px;
  border: dashed white 1px;
  padding: 3px 9px;
  margin: 24px 1px 16px 1px;
  display: block;
  align-items: center;
  font-weight:bold;
  text-align:center;
  color: white;
}

.data-section::before {
  content: attr(data-label);
  display: inline-block;
  white-space: pre-wrap;
}

.disabled-image {
  pointer-events: none;
  touch-action: none;
}

.error-hint {
  word-break: break-word;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1;
  font-size: 12px;
  color: var(--app-error);
  padding: 8px 12px;
}

.error-outline {
  outline: 2px solid var(--app-error);
}
</style>
